import React from 'react';
import { alpha, Grid, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { AreaOccupancyData } from '../../../api';
import { slotAt, slotStartSeconds } from '../utils/occupancyMath';
import { AREAS_PALETTE, slotBackground, EMPTY_SLOT_BACKGROUND } from '../areasPalette';
import { formatSeconds } from '../utils/areasFormat';

/** Roughly how many hour labels to show along a strip. */
const LABEL_COUNT_TARGET = 6;

/** A phone strip only has room for a few anchors. */
const MOBILE_LABEL_COUNT_TARGET = 3;

/** Cells to keep clear either side of the selected label so the two do not run together. */
const LABEL_MIN_GAP = 2;

type AreaTimelineStripProps = {
  area: AreaOccupancyData;
  slotMinutes: number;
  /** Orders starting in each slot, keyed by slot index. */
  arrivals: Record<number, number>;
  startSlot: number;
  endSlot: number;
  /** The area's own bookable hours; cells outside them draw as closed. Defaults to the whole axis. */
  openStartSlot?: number;
  openEndSlot?: number;
  selectedSlot: number;
  nowSlot: number | null;
  timeFormat: string;
  onSlotClick: (seconds: number, cell: HTMLElement) => void;
};

/**
 * The day at a glance: one cell per slot, coloured by how much room is left, so "when IS there
 * room today" is answerable without moving the clock. Clicking a cell pins that time.
 */
export const AreaTimelineStrip = ({
  area,
  slotMinutes,
  arrivals,
  startSlot,
  endSlot,
  openStartSlot = startSlot,
  openEndSlot = endSlot,
  selectedSlot,
  nowSlot,
  timeFormat,
  onSlotClick,
}: AreaTimelineStripProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  /**
   * On a phone the strip scrolls sideways with 24px-wide tappable cells and only a few labelled anchors.
   */
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const slots = [];
  for (let slot = startSlot; slot <= endSlot; slot++) {
    slots.push(slot);
  }

  /**
   * A bare band of colour does not say which hour is which. Label a handful of evenly spaced
   * slots — always the first and last — so the rest can be read by position.
   */
  const labelStep = Math.max(
    1,
    Math.ceil(slots.length / (isSmallScreen ? MOBILE_LABEL_COUNT_TARGET : LABEL_COUNT_TARGET)),
  );
  const lastIndex = slots.length - 1;
  const selectedPosition = slots.indexOf(selectedSlot);

  /** The selected slot always names itself; regular labels give way rather than collide with it. */
  const isLabelled = (position: number) => {
    if (position === selectedPosition) {
      return true;
    }
    if (selectedPosition >= 0 && Math.abs(position - selectedPosition) < LABEL_MIN_GAP) {
      return false;
    }
    return (
      position === 0 ||
      position === lastIndex ||
      // Skip a regular label that would crowd the final one.
      (position % labelStep === 0 && lastIndex - position >= Math.ceil(labelStep / 2))
    );
  };

  return (
    <Grid
      container
      wrap="nowrap"
      gap="2px"
      // Cells hang from a common top edge so the selected one can stand taller without
      // dragging its neighbours' blocks down with it.
      sx={{ overflowX: 'auto', py: 0.5, px: 0.5, alignItems: 'flex-start' }}
      role="group"
      aria-label={t('calendar.areas_view.timeline_label')}>
      {slots.map((slot, position) => {
        const slotData = slotAt(area, slot);
        const isOutsideHours = slot < openStartSlot || slot > openEndSlot;
        const background = isOutsideHours ? AREAS_PALETTE.blockedHatch : slotBackground(slotData);
        const seconds = slotStartSeconds(slot, slotMinutes);
        const label = formatSeconds(seconds, timeFormat);
        // Labels name slot starts, except the last one: hugging the end edge, it names the closing time,
        // so the axis reads 09:00 … 14:00 rather than ending on a 13:30 that means "until 14:00".
        const axisLabel =
          position === lastIndex ? formatSeconds(slotStartSeconds(slot + 1, slotMinutes), timeFormat) : label;
        const free = slotData?.free ?? 0;
        const blocked = slotData?.blocked ?? 0;
        const isSelected = slot === selectedSlot;
        const arriving = arrivals[slot] ?? 0;
        const description = [
          label,
          isOutsideHours ? t('calendar.areas_view.closed') : t('calendar.areas_view.free_count', { count: free }),
          blocked > 0 ? t('calendar.areas_view.blocked_chip', { count: blocked }) : '',
          arriving > 0 ? t('calendar.areas_view.starting_count', { count: arriving }) : '',
        ].filter(Boolean);
        const tooltip = description.join(' · ');
        return (
          <Grid
            key={slot}
            sx={{
              flex: isSmallScreen ? '0 0 24px' : '1 0 10px',
              minWidth: isSmallScreen ? '24px' : '10px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
            }}>
            <Typography
              sx={{
                fontSize: isSmallScreen ? '10px' : '11px',
                height: '11px',
                lineHeight: '11px',
                // Clear the selected cell's outline, which sits 3px proud of the block
                // (2px wide at a 1px offset) and otherwise nearly touches the label.
                marginBottom: isSmallScreen ? '3px' : '5px',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
                // Edge labels hug their own edge so they are not clipped by the strip.
                textAlign: position === 0 ? 'start' : position === lastIndex ? 'end' : 'center',
                ...(isSelected && { color: theme.palette.primary.main, fontWeight: 700 }),
              }}>
              {isLabelled(position) ? axisLabel : ''}
            </Typography>
            <Tooltip title={tooltip}>
              <Grid
                component="button"
                type="button"
                aria-label={description.join(', ')}
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                  event.stopPropagation();
                  onSlotClick(seconds, event.currentTarget);
                }}
                sx={{
                  width: '100%',
                  // The selected slot stands taller than its neighbours — a size difference reads
                  // faster than an outline alone.
                  height: isSmallScreen ? (isSelected ? '20px' : '14px') : isSelected ? '32px' : '22px',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  borderRadius: '3px',
                  background,
                  outline: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
                  outlineOffset: '1px',
                  // Selection glow and the "now" underline are separate signals that can land on
                  // the same cell, so both are kept.
                  boxShadow:
                    [
                      isSelected ? `0 2px 6px ${alpha(theme.palette.primary.main, 0.33)}` : '',
                      slot === nowSlot ? `inset 0 -3px 0 0 ${AREAS_PALETTE.nowMarker}` : '',
                      background === EMPTY_SLOT_BACKGROUND ? `inset 0 0 0 1px ${theme.palette.divider}` : '',
                    ]
                      .filter(Boolean)
                      .join(', ') || 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2px',
                  overflow: 'hidden',
                }}>
                {arriving > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '11px',
                      fontWeight: 700,
                      lineHeight: 1,
                      color: 'text.primary',
                      fontVariantNumeric: 'tabular-nums',
                      pointerEvents: 'none',
                    }}>
                    {arriving}
                  </Typography>
                )}
                {!isSmallScreen && blocked > 0 && (
                  <LockIcon sx={{ fontSize: '11px', color: 'text.secondary', pointerEvents: 'none' }} />
                )}
              </Grid>
            </Tooltip>
          </Grid>
        );
      })}
    </Grid>
  );
};
